import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import './App.css';

const { FiPhone, FiMail, FiMapPin, FiStar, FiCheck, FiClock, FiShield, FiUsers, FiTool, FiWind, FiRefreshCw, FiSettings } = FiIcons;

function App() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    serviceType: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    alert('Thank you! We will contact you soon to schedule your service with 15% off!');
  };

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId).scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="hero-section relative overflow-hidden">
        <div className="hero-background"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center lg:text-left"
            >
              <h1 className="hero-headline mb-6">
                Stay Cool and Save with Build Pro Cooling & HVAC!
              </h1>
              <p className="hero-subheadline mb-6">
                Tired of sweaty homes or high energy bills? Our HVAC services keep South Florida cool.
              </p>
              <p className="hero-description mb-8">
                A broken AC ruins your day. Hot rooms and costly repairs hurt. Build Pro Cooling & HVAC fixes your system fast. We make homes and businesses cool, comfortable, and energy-efficient.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => scrollToSection('contact')}
                  className="cta-primary"
                >
                  Book Now & Save 15%
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => scrollToSection('services')}
                  className="cta-secondary"
                >
                  View Our Services
                </motion.button>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="hero-image-container">
                <img
                  src="https://images.unsplash.com/photo-1621905251189-08b45d6a269e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2069&q=80"
                  alt="Professional HVAC technician working on air conditioning unit"
                  className="hero-image"
                />
                <div className="hero-badge">
                  <SafeIcon icon={FiShield} className="text-white text-2xl mr-2" />
                  <span>Licensed & Insured</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-headline mb-6">
              Your Trusted HVAC Experts in South Florida
            </h2>
            <p className="section-description max-w-4xl mx-auto">
              For over 15 years, Build Pro Cooling & HVAC has kept South Florida cool. We understand your need for reliable cooling. Our licensed and insured team delivers quality service. Trust us to solve your HVAC problems.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="feature-card"
            >
              <SafeIcon icon={FiClock} className="feature-icon" />
              <h3 className="feature-title">15+ Years Experience</h3>
              <p className="feature-description">
                Trusted by South Florida homeowners and businesses for over 15 years.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="feature-card"
            >
              <SafeIcon icon={FiShield} className="feature-icon" />
              <h3 className="feature-title">Licensed & Insured</h3>
              <p className="feature-description">
                Fully licensed (CMC#1251497) and insured for your peace of mind.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="feature-card"
            >
              <SafeIcon icon={FiUsers} className="feature-icon" />
              <h3 className="feature-title">Expert Team</h3>
              <p className="feature-description">
                Skilled technicians who care about your comfort and satisfaction.
              </p>
            </motion.div>
          </div>

          <div className="text-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => scrollToSection('services')}
              className="cta-primary"
            >
              Discover Our Services
            </motion.button>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-headline mb-6">
              Solutions for ALL Your Cooling Needs
            </h2>
            <p className="section-description max-w-3xl mx-auto">
              Don't wait for a breakdown—book your service now and keep your home or business comfortable year-round.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="service-card"
            >
              <SafeIcon icon={FiTool} className="service-icon" />
              <h3 className="service-title">Routine Maintenance</h3>
              <p className="service-description">
                Prevent breakdowns and save on energy bills with regular check-ups.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="service-card"
            >
              <SafeIcon icon={FiWind} className="service-icon" />
              <h3 className="service-title">HVAC Cleaning</h3>
              <p className="service-description">
                Clean systems mean fresh air and lower costs for your home or business.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="service-card"
            >
              <SafeIcon icon={FiRefreshCw} className="service-icon" />
              <h3 className="service-title">HVAC Replacement</h3>
              <p className="service-description">
                Upgrade old systems to efficient ones that cool better and save money.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="service-card"
            >
              <SafeIcon icon={FiSettings} className="service-icon" />
              <h3 className="service-title">Mechanical Services</h3>
              <p className="service-description">
                We fix any cooling or mechanical issue with precision and care.
              </p>
            </motion.div>
          </div>

          <div className="text-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => scrollToSection('contact')}
              className="cta-primary"
            >
              Schedule Service
            </motion.button>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-headline mb-6">
              Happy Clients Trust Build Pro
            </h2>
            <p className="section-description max-w-3xl mx-auto">
              Join thousands of satisfied customers who trust Build Pro for their HVAC needs.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="testimonial-card"
            >
              <div className="testimonial-stars">
                {[...Array(5)].map((_, i) => (
                  <SafeIcon key={i} icon={FiStar} className="star-icon" />
                ))}
              </div>
              <p className="testimonial-text">
                "My AC broke in the middle of the summer. Build Pro fixed it the same day! Professional and reliable service."
              </p>
              <div className="testimonial-author">
                <strong>Maria S.</strong>
                <span>Miami</span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="testimonial-card"
            >
              <div className="testimonial-stars">
                {[...Array(5)].map((_, i) => (
                  <SafeIcon key={i} icon={FiStar} className="star-icon" />
                ))}
              </div>
              <p className="testimonial-text">
                "Their maintenance service cut my energy bill almost in half! Amazing results and great service."
              </p>
              <div className="testimonial-author">
                <strong>John C.</strong>
                <span>Fort Lauderdale</span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="testimonial-card"
            >
              <div className="testimonial-stars">
                {[...Array(5)].map((_, i) => (
                  <SafeIcon key={i} icon={FiStar} className="star-icon" />
                ))}
              </div>
              <p className="testimonial-text">
                "Professional and fast. Our office is cool again! Highly recommend Build Pro for commercial HVAC."
              </p>
              <div className="testimonial-author">
                <strong>Lisa R.</strong>
                <span>Boca Raton</span>
              </div>
            </motion.div>
          </div>

          <div className="text-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => scrollToSection('contact')}
              className="cta-primary"
            >
              Get Your Quote
            </motion.button>
          </div>
        </div>
      </section>

       {/* FAQs Section */}
      <section id="faqs" className="py-20 bg-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="section-headline mb-6 text-blue-900">Frequently Asked Questions</h2>
            <p className="section-description max-w-3xl mx-auto">
              Here are some common questions South Florida homeowners ask about HVAC services:
            </p>
          </motion.div>

          <div className="space-y-6">
            <details className="faq-item p-4 border rounded bg-white">
              <summary className="faq-question font-semibold text-blue-900 cursor-pointer">
                How quickly can you come if my AC breaks?
              </summary>
              <p className="faq-answer mt-2 text-gray-700">
                We aim for same-day service in most of Broward, Miami-Dade, and Palm Beach. Emergencies are prioritized.
              </p>
            </details>

            <details className="faq-item p-4 border rounded bg-white">
              <summary className="faq-question font-semibold text-blue-900 cursor-pointer">
                Do you handle both residential and commercial HVAC?
              </summary>
              <p className="faq-answer mt-2 text-gray-700">
                Yes — from single-family homes to large commercial spaces, our licensed team handles all HVAC systems.
              </p>
            </details>

            <details className="faq-item p-4 border rounded bg-white">
              <summary className="faq-question font-semibold text-blue-900 cursor-pointer">
                How do I know if my HVAC system needs replacement?
              </summary>
              <p className="faq-answer mt-2 text-gray-700">
                Signs include frequent breakdowns, uneven cooling, strange noises, or high energy bills. We provide honest assessments.
              </p>
            </details>

            <details className="faq-item p-4 border rounded bg-white">
              <summary className="faq-question font-semibold text-blue-900 cursor-pointer">
                Are you licensed and insured in Florida?
              </summary>
              <p className="faq-answer mt-2 text-gray-700">
                Yes. Build Pro Cooling & HVAC is fully licensed (License #CMC1251497) and insured for peace of mind.
              </p>
            </details>
          </div>
        </div>
      </section>

      {/* Offer Section */}
      <section id="offer" className="py-20 bg-gradient-to-r from-blue-900 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="offer-badge mb-8">
              <span className="offer-text">Save 15% on Your First Service!</span>
            </div>
            <h2 className="offer-headline mb-6">
              Hot home? High bills? Book your first service now and get 15% off.
            </h2>
            <p className="offer-description mb-8 max-w-3xl mx-auto">
              Our team ensures your HVAC runs smoothly. Limited spots available. Act fast to lock in your discount!
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => scrollToSection('contact')}
              className="cta-offer"
            >
              Grab Your Offer
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="section-headline mb-6">
              Get Your HVAC Fixed Today!
            </h2>
            <p className="section-description max-w-3xl mx-auto">
              Fill out this form to book your service. Share your details and service type. Get 15% off your first service when you book now. Don't let a broken AC ruin your day!
            </p>
          </motion.div>

          {/* CONTACT FORM (Formspree) */}
<section id="contact" className="py-20 bg-white">
  <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
    <h2 className="section-headline mb-6 text-center">Get Your HVAC Fixed Today!</h2>
    <p className="section-description max-w-3xl mx-auto text-center mb-10">
      Fill out this form to book your service. Get 15% off your first HVAC service when you book now.
    </p>

    {/* ✅ Direct Formspree integration */}
    <form
      action="https://formspree.io/f/xjkelivn"   // 👈 your HVAC Formspree endpoint
      method="POST"
      className="space-y-5"
    >
      <input type="text" name="name" placeholder="Full Name" required className="w-full px-4 py-3 rounded border" />
      <input type="tel" name="phone" placeholder="Phone Number" required className="w-full px-4 py-3 rounded border" />
      <input type="email" name="email" placeholder="Email Address" required className="w-full px-4 py-3 rounded border" />
      <input type="text" name="address" placeholder="Service Address" required className="w-full px-4 py-3 rounded border" />

      <select name="serviceType" required className="w-full px-4 py-3 rounded border">
        <option value="">Select a service</option>
        <option value="routine-maintenance">Routine Maintenance</option>
        <option value="hvac-cleaning">HVAC Cleaning</option>
        <option value="hvac-replacement">HVAC Replacement</option>
        <option value="other-mechanical">Other Mechanical Services</option>
      </select>

      {/* ✅ Redirect after submit */}
      <input type="hidden" name="_redirect" value="https://www.buildprofl.com/" />

      {/* ✅ Subject line in your email */}
      <input type="hidden" name="_subject" value="Cooling & HVAC Lead" />

      {/* ✅ Honeypot spam trap */}
      <input type="text" name="_gotcha" tabIndex="-1" autoComplete="off" className="hidden" style={{ display: "none" }} />

      <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded w-full">
        Send My Request & Save 15%
      </button>

      {/* ✅ Legal notice under form */}
      <p className="text-xs text-gray-500 mt-3">
        By submitting, you agree to receive calls and texts (including automated and pre-recorded) about your request.
        Consent is not required to purchase. Msg/data rates may apply. Reply STOP to opt out.
      </p>
    </form>
  </div>
</section>

        </div>
      </section>

      {/* Footer with Privacy/Legal */}
<footer className="bg-blue-900 text-white py-12">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="grid md:grid-cols-3 gap-8 mb-8">
      <div>
        <h3 className="footer-title mb-4">Build Pro Cooling & HVAC</h3>
        <p className="footer-text">
          Part of Build Pro Construction - serving South Florida for over 15 years.
        </p>
      </div>

      <div>
        <h4 className="footer-subtitle mb-4">Contact Info</h4>
        <p>5273 SW 92nd Terr, Cooper City, FL 33328</p>
        <p>info@buildprofl.com</p>
        <p>License #CMC1251497</p>
      </div>

      <div>
        <h4 className="footer-subtitle mb-4">Our Services</h4>
        <ul className="footer-services">
          <li>Routine Maintenance</li>
          <li>HVAC Cleaning</li>
          <li>HVAC Replacement</li>
          <li>Mechanical Services</li>
        </ul>
      </div>
    </div>

    {/* Expanded Legal/Privacy in Footer */}
    <div id="privacy" className="text-xs text-gray-300 leading-relaxed space-y-2">
      <p><strong>Privacy Policy:</strong> We collect your contact details (name, phone, email, message)
      to provide quotes and service updates. We do not sell your data. You can request access or deletion
      anytime by contacting info@buildprofl.com.</p>

      <p><strong>SMS Policy:</strong> By providing your phone number, you agree to receive automated and/or
      pre-recorded calls and texts about your request. Consent not required to purchase. Message/data rates
      may apply. Reply STOP to opt out.</p>

      <p id="terms"><strong>Terms of Use:</strong> Services are subject to Florida law and our service
      agreements. Licensed contractor #CMC1251497.</p>
    </div>

    <div className="footer-bottom text-center mt-6 text-gray-400 text-sm">
      &copy; 2024 Build Pro Cooling & HVAC. All rights reserved.
    </div>
  </div>
</footer>
    </div>
  );
}

export default App;
